import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityWorkspace from '../components/EntityWorkspace';
import EntityPivotLink from '../components/EntityPivotLink';
import DeleteButton from '../components/DeleteButton';
import api from '../services/api';

function Organizations() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    org_type: 'company',
    country: '',
    sector: '',
    description: '',
  });
  const [creating, setCreating] = useState(false);

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Organization',
      render: (row) => (
        <EntityPivotLink type="organization" id={row.organization_id} label={row.name} showBadge={false} />
      ),
    },
    { key: 'type', label: 'Type', render: (row) => <span className="entity-type-chip">{row.org_type}</span> },
    { key: 'country', label: 'Country', render: (row) => row.country || '—' },
    { key: 'sector', label: 'Sector', render: (row) => row.sector || '—' },
    {
      key: 'labels',
      label: 'Labels',
      render: (row) => (
        <div className="entity-tag-list">
          {(row.labels || []).slice(0, 3).map((tag) => (
            <span key={tag} className="entity-tag">{tag}</span>
          ))}
        </div>
      ),
    },
  ], []);

  const filterFields = useMemo(() => [
    { key: 'org_type', label: 'Type', options: (meta) => meta?.org_types || [] },
    { key: 'sector', label: 'Sector', options: (meta) => meta?.org_sectors || [] },
  ], []);

  const submitCreate = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await api.post('/hdp/organizations', form);
      setCreateOpen(false);
      setForm({ name: '', org_type: 'company', country: '', sector: '', description: '' });
      window.location.reload();
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <EntityWorkspace
        title="Organizations"
        subtitle="Companies, hosting providers, and infrastructure owners referenced in investigations."
        breadcrumbs={[
          { label: 'Dashboard', to: '/' },
          { label: 'Organizations' },
        ]}
        entityType="organization"
        apiPath="/hdp/organizations"
        responseKey="organizations"
        rowIdField="organization_id"
        filterFields={filterFields}
        columns={columns}
        headerActions={(
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            Create organization
          </button>
        )}
        onRowClick={(row) => navigate(`/organizations/${row.organization_id}`)}
        rowActions={(row, reload) => (
          <DeleteButton
            confirmMessage={`Delete organization "${row.name}"?`}
            onDelete={async () => {
              await api.delete(`/hdp/organizations/${row.organization_id}`);
              reload();
            }}
          />
        )}
        emptyMessage="No organizations match your filters."
      />

      {createOpen && (
        <div className="modal-backdrop" onClick={() => setCreateOpen(false)}>
          <div className="modal-card entity-create-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create organization</h3>
            <form onSubmit={submitCreate}>
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
              <label className="form-label">Type</label>
              <select className="form-input" value={form.org_type} onChange={(e) => setForm((p) => ({ ...p, org_type: e.target.value }))}>
                <option value="company">Company</option>
                <option value="hosting">Hosting</option>
                <option value="financial">Financial</option>
                <option value="government">Government</option>
                <option value="criminal">Criminal</option>
                <option value="unknown">Unknown</option>
              </select>
              <label className="form-label">Country</label>
              <input className="form-input" value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} />
              <label className="form-label">Sector</label>
              <input className="form-input" value={form.sector} onChange={(e) => setForm((p) => ({ ...p, sector: e.target.value }))} />
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Organizations;
